import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { isExternalVideo } from '../../../../common/ui-selectors/video/video-origin';
import { Select } from '../../../select/select';
import { getChannelContent, getVideoContent } from './utils/content';
import { Title } from './title/title';
import { Facebook } from './facebook/facebook';
import { Twitter } from './twitter/twitter';
import { Pinterest } from './pinterest/pinterest';
import { Tumblr } from './tumblr/tumblr';
import { Link, LinkCopied } from './link/link';
import { EmbedButton } from './embed-button/embed-button';
import { More } from './more/more';
import { SHARE_MODES } from '../../../../common/constants/share-modes';
import styles from './styles.scss';
const HIDE_MESSAGE_TIMEOUT = 3000;
const stopPropagation = (e) => {
    e.stopPropagation();
};
export class ShareButtons extends React.Component {
    constructor(props) {
        super(props);
        this.handleChange = (event) => {
            this.setState({
                shareMode: event.target.value,
            });
        };
        this.handleLinkClick = () => {
            const { showLinkCopied } = this.state;
            // do not allow to copy link during next 3 sec after first attempt
            if (showLinkCopied) {
                return;
            }
            this.setState({ showLinkCopied: true });
            setTimeout(() => {
                this.setState({ showLinkCopied: false });
            }, HIDE_MESSAGE_TIMEOUT);
            this.props.onLinkCopied({
                shareType: this.isInShareChannelMode()
                    ? SHARE_MODES.CHANNEL
                    : SHARE_MODES.VIDEO,
            });
        };
        this.handleEmbedClick = () => {
            this.props.onEmbedClicked({
                shareType: this.isInShareChannelMode()
                    ? SHARE_MODES.CHANNEL
                    : SHARE_MODES.VIDEO,
            });
        };
        this.handleShare = (networkName) => {
            this.props.onShare({
                networkName,
                shareType: this.isInShareChannelMode()
                    ? SHARE_MODES.CHANNEL
                    : SHARE_MODES.VIDEO,
            });
        };
        this.state = {
            shareMode: props.shareMode,
            showLinkCopied: false,
        };
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { shareMode } = this.props;
        if (nextProps.shareMode !== shareMode) {
            this.setState({ shareMode: nextProps.shareMode });
        }
    }
    isInShareChannelMode() {
        return this.state.shareMode === SHARE_MODES.CHANNEL;
    }
    isEmbedButtonHidden() {
        const { videoItem } = this.props;
        return this.isInShareChannelMode() || isExternalVideo(videoItem);
    }
    getShareOptions() {
        const { shareChannelText, shareVideoText } = this.props;
        return [
            {
                value: SHARE_MODES.CHANNEL,
                text: shareChannelText,
            },
            {
                value: SHARE_MODES.VIDEO,
                text: shareVideoText,
            },
        ];
    }
    getContentToShare() {
        if (this.isInShareChannelMode()) {
            const { channelData } = this.props;
            return getChannelContent(channelData);
        }
        else {
            const { videoItem } = this.props;
            return getVideoContent(videoItem);
        }
    }
    getInstanceInfo() {
        const { channelUrl, videoUrl, instanceInfo } = this.props;
        return {
            ...instanceInfo,
            siteUrl: this.isInShareChannelMode() ? channelUrl : videoUrl,
        };
    }
    shouldShowEmbedButton() {
        const { shouldShowEmbedButton } = this.props;
        return shouldShowEmbedButton && !this.isEmbedButtonHidden();
    }
    render() {
        const { titleText, children, channelData, videoItem, onMoreClicked, facebookAriaLabel, twitterAriaLabel, pinterestAriaLabel, tumblrAriaLabel, linkAriaLabel, embedAriaLabel, moreAriaLabel, embedTooltipText, facebookTooltipText, linkTooltipText, moreTooltipText, pinterestTooltipText, tumblrTooltipText, twitterTooltipText, canShareContent, canShowSelect, shouldShowFacebookButton, shouldShowTwitterButton, shouldShowPinterestButton, shouldShowTumblrButton, shouldShowLinkButton, shouldShowMoreButton, isRTL, twitterDescriptionText, pinterestDescriptionText, tumblrDescriptionText, linkCopiedText, } = this.props;
        if (children) {
            return children;
        }
        const { shareMode, showLinkCopied } = this.state;
        const videoId = this.isInShareChannelMode() ? null : videoItem.id;
        const channelId = channelData.id;
        const contentToShare = this.getContentToShare();
        const instanceInfo = this.getInstanceInfo();
        return (React.createElement("section", { className: styles.container, onClick: stopPropagation },
            React.createElement(Title, { text: titleText, dataHook: "title" }),
            canShowSelect && (React.createElement(Select, { dataHook: "share-select", className: styles.select, options: this.getShareOptions(), selected: shareMode, onChange: this.handleChange, isRTL: isRTL })),
            React.createElement("div", { "data-hook": "share-buttons", className: styles['share-buttons'] },
                shouldShowFacebookButton && (React.createElement(Facebook, { canShareContent: canShareContent, dataHook: "facebook", tooltip: facebookTooltipText, onShare: this.handleShare, videoId: videoId, channelId: channelId, content: contentToShare, instanceInfo: instanceInfo, ariaLabel: facebookAriaLabel })),
                shouldShowTwitterButton && (React.createElement(Twitter, { canShareContent: canShareContent, dataHook: "twitter", tooltip: twitterTooltipText, onShare: this.handleShare, descriptionText: twitterDescriptionText, videoId: videoId, channelId: channelId, content: contentToShare, instanceInfo: instanceInfo, ariaLabel: twitterAriaLabel })),
                shouldShowPinterestButton && (React.createElement(Pinterest, { canShareContent: canShareContent, dataHook: "pinterest", tooltip: pinterestTooltipText, onShare: this.handleShare, content: contentToShare, instanceInfo: instanceInfo, descriptionText: pinterestDescriptionText, ariaLabel: pinterestAriaLabel })),
                shouldShowTumblrButton && (React.createElement(Tumblr, { canShareContent: canShareContent, className: styles.tumblr, dataHook: "tumblr", tooltip: tumblrTooltipText, onShare: this.handleShare, content: contentToShare, instanceInfo: instanceInfo, descriptionText: tumblrDescriptionText, ariaLabel: tumblrAriaLabel })),
                shouldShowLinkButton && (React.createElement(Link, { canShareContent: canShareContent, dataHook: "link", onClick: this.handleLinkClick, tooltip: linkTooltipText, instanceInfo: instanceInfo, ariaLabel: linkAriaLabel })),
                this.shouldShowEmbedButton() && (React.createElement(EmbedButton, { dataHook: "embed-button", onClick: this.handleEmbedClick, tooltip: embedTooltipText, ariaLabel: embedAriaLabel })),
                shouldShowMoreButton && (React.createElement(More, { dataHook: "more-button", onClick: onMoreClicked, tooltip: moreTooltipText, ariaLabel: moreAriaLabel }))),
            canShareContent && (React.createElement(LinkCopied, { dataHook: "link-copied", shouldShowLink: showLinkCopied, text: linkCopiedText, isRTL: isRTL }))));
    }
}
ShareButtons.propTypes = {
    shareMode: PropTypes.oneOf([SHARE_MODES.VIDEO, SHARE_MODES.CHANNEL]),
    titleText: PropTypes.string.isRequired,
    children: PropTypes.node,
    channelData: PropTypes.object,
    videoItem: PropTypes.object,
    instanceInfo: PropTypes.object,
    videoUrl: PropTypes.string,
    channelUrl: PropTypes.string,
    canShareContent: PropTypes.bool,
    canShowSelect: PropTypes.bool,
    shouldShowFacebookButton: PropTypes.bool,
    shouldShowTwitterButton: PropTypes.bool,
    shouldShowPinterestButton: PropTypes.bool,
    shouldShowTumblrButton: PropTypes.bool,
    shouldShowLinkButton: PropTypes.bool,
    shouldShowEmbedButton: PropTypes.bool,
    shouldShowMoreButton: PropTypes.bool,
    isRTL: PropTypes.bool,
    shareChannelText: PropTypes.string,
    shareVideoText: PropTypes.string,
    twitterDescriptionText: PropTypes.string,
    pinterestDescriptionText: PropTypes.string,
    tumblrDescriptionText: PropTypes.string,
    linkCopiedText: PropTypes.string,
    // aria labels
    facebookAriaLabel: PropTypes.string,
    twitterAriaLabel: PropTypes.string,
    pinterestAriaLabel: PropTypes.string,
    tumblrAriaLabel: PropTypes.string,
    linkAriaLabel: PropTypes.string,
    embedAriaLabel: PropTypes.string,
    moreAriaLabel: PropTypes.string,
    // tooltips
    embedTooltipText: PropTypes.string,
    facebookTooltipText: PropTypes.string,
    linkTooltipText: PropTypes.string,
    moreTooltipText: PropTypes.string,
    pinterestTooltipText: PropTypes.string,
    tumblrTooltipText: PropTypes.string,
    twitterTooltipText: PropTypes.string,
    // callbacks
    onShare: PropTypes.func,
    onEmbedClicked: PropTypes.func,
    onMoreClicked: PropTypes.func,
    onLinkCopied: PropTypes.func,
};
ShareButtons.defaultProps = {
    canShareContent: true,
    canShowSelect: true,
    shouldShowFacebookButton: true,
    shouldShowTwitterButton: true,
    shouldShowPinterestButton: true,
    shouldShowTumblrButton: true,
    shouldShowLinkButton: true,
    shouldShowEmbedButton: false,
    shouldShowMoreButton: false,
    twitterDescriptionText: '',
    pinterestDescriptionText: '',
    shareMode: SHARE_MODES.CHANNEL,
    onShare: noop,
    onEmbedClicked: noop,
    onMoreClicked: noop,
    onLinkCopied: noop,
};
//# sourceMappingURL=share-buttons.js.map